<template>
	<CCallout v-if="error" type="error" class="global-errors print:hidden">
		<template v-if="error.text">
			<span v-html="error.text"></span>
		</template>
		<template #button>
			<RouterLink
				v-if="error.link && isOwner"
				:to="error.link"
				target="_top"
				class="button button--primary"
			>
				{{ error.buttonText }}
			</RouterLink>
			<CStatusButton v-else-if="error.action" class="button button--primary" :status="actionButtonStatus" @click="handleActionButton">
				{{ error.buttonText }}
			</CStatusButton>
		</template>
	</CCallout>
</template>

<script lang="ts">
	import { computed, defineComponent, ref, type PropType, watch } from 'vue';
	import { useRouter } from 'vue-router/composables';
	import type { GlobalStore } from 'src/stores/global-store';
	import { GlobalError } from '@cognitoforms/types/server-types/core/services/global-error';
	import CCallout from 'src/components/content/Callout.vue';
	import { resendUserVerificationEmail } from '@cognitoforms/api/services/authentication';
	import CStatusButton, { type ButtonStatus } from 'src/components/interaction/StatusButton.vue';
	import session from '@cognitoforms/api/services/session';

	export default defineComponent({
		name: 'CGlobalErrors',
		components: {
			CCallout,
			CStatusButton
		},
		props: {
			globalStore: {
				type: Object as PropType<GlobalStore>,
				required: true
			},
			errorFilter: {
				type: Array as PropType<GlobalError[]>,
				default: null
			},
			errorExclude: {
				type: Array as PropType<GlobalError[]>,
				default: null
			}
		},
		setup(props) {
			const user = computed (() => {
				return props.globalStore.user;
			});

			const org = computed(() => {
				return props.globalStore.organization;
			});

			const isOwner = computed(() => org.value.Role === 'Owner');

			const actionButtonStatus = ref<ButtonStatus>('default');

			const error = computed (() => {
				if (props.errorFilter && !props.errorFilter.includes(org.value.CurrentError))
					return null;

				if (props.errorExclude?.includes(org.value.CurrentError))
					return null;

				if (!user.value.IsVerified && user.value.Email)
					return {
						text: `Thanks for signing up. We've sent an email to ${ user.value.Email } with a link to verify your account.`,
						action: resendUserVerificationEmail,
						buttonText: 'Resend Now'
					};
				else if (org.value.CurrentError === GlobalError.BillingFailure)
					return {
						text: `The most recent attempts at paying this month's invoice for your ${ org.value.Plan.Name } plan has failed. Please review your billing information.`,
						link: `/${org.value.Code}/settings/plan`,
						buttonText: 'View Billing Information'
					};
				else if (org.value.CurrentError === GlobalError.BillingPastDue)
					return {
						text: 'The most recent attempt at paying your latest invoice has failed. Please review your billing information and pay the invoice to avoid service interruptions.'
							+ (isOwner.value ? '' : ' Please contact the organization owner.'),
						link: `/${org.value.Code}/settings/plan`,
						buttonText: 'Review & Pay'
					};
				else if (org.value.CurrentError === GlobalError.BillingRequiresAuthentication)
					return {
						text: `The most recent attempts at paying this month's invoice for your ${ org.value.Plan.Name } plan has failed due to requiring additional authentication. You should have received an email to complete the authentication.`,
						link: null,
						buttonText: null
					};
				else if (org.value.CurrentError === GlobalError.BillingRestricted) {
					const downgradeDate = org.value.DowngradeAt ? new Date(org.value.DowngradeAt) : null;
					const hasDowngradeDate = downgradeDate && !Number.isNaN(downgradeDate.valueOf());
					return {
						text: 'Your organization is restricted due to a past-due invoice. '
							+ (hasDowngradeDate
								? `Please pay the invoice by ${downgradeDate.toLocaleDateString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: 'UTC' })} to restore full access to your organization before it is downgraded.`
								: 'Please pay the invoice to restore full access to your organization before it is downgraded.')
							+ (isOwner.value ? '' : ' Please contact the organization owner.'),
						link: `/${org.value.Code}/settings/plan#invoices`,
						buttonText: 'Pay Invoice'
					};
				}
				else if (org.value.CurrentError === GlobalError.MissingTaxInformation)
					return {
						text: '<strong>Action required: Tax information</strong> - To issue accurate and compliant invoices, we need your organization’s tax information. This also helps prevent being charged sales tax incorrectly.',
						link: `/${org.value.Code}/settings/plan?edit=true`,
						buttonText: 'Edit Billing Information'
					};
				else if (org.value.CurrentError === GlobalError.EntryLimitExceeded)
					return {
						text: 'You’re over your monthly entry limit. To access new entries, upgrade your plan now.',
						link: `/${org.value.Code}/settings/plan/select?source=unavailfeat&details=entrylimit`,
						buttonText: 'Upgrade Now'
					};
				else if (org.value.CurrentError === GlobalError.StorageLimitExceeded)
					return {
						text: 'You’re over your storage capacity. Free up some space or upgrade your plan for additional storage.',
						link: `/${org.value.Code}/settings/plan/select?source=unavailfeat&details=storagelimit`,
						buttonText: 'Upgrade Now'
					};
				else if (org.value.CurrentError === GlobalError.HasLockedUsers)
					return {
						text: 'The Individual plan includes one user (you!), but you have additional users and they can no longer access your forms. Upgrade your plan to restore their access.',
						link: `/${org.value.Code}/settings/plan/select?source=unavailfeat&details=users`,
						buttonText: 'Upgrade Now'
					};
				else if (org.value.CurrentError === GlobalError.HasLockedEmailDomains)
					return {
						text: 'Your current plan does not support custom email domains. Please upgrade to the Team plan or higher to re-enable your verified email domains.',
						link: `/${org.value.Code}/settings/plan/select?source=unavailfeat&details=domains`,
						buttonText: 'Upgrade Now'
					};
				else if (org.value.CurrentError === GlobalError.PaymentAccountDisabled)
					return {
						text: (org.value.DisabledCognitoPayAccountIds?.length > 1
							? 'Action required to resume processing payments and payouts on multiple accounts.'
							: 'Action required to resume processing payments and payouts.')
							+ (isOwner.value ? '' : ' Please contact the organization owner.'),
						link: `/${org.value.Code}/payment/account${org.value.DisabledCognitoPayAccountIds?.length ? `?accountId=${org.value.DisabledCognitoPayAccountIds[0]}` : ''}`,
						buttonText: 'Manage Account'
					};
				else if (org.value.CurrentError === GlobalError.PayoutsDisabled)
					return {
						text: 'Action required: update your bank account to resume payouts.'
							+ (isOwner.value ? '' : ' Please contact the organization owner.'),
						link: `/${org.value.Code}/payment/payouts${org.value.DisabledCognitoPayAccountIds?.length ? `?accountId=${org.value.DisabledCognitoPayAccountIds[0]}` : ''}`,
						buttonText: 'Manage Payouts'
					};

				return null;
			});

			const router = useRouter();
			watch(() => org.value.CurrentError, (currentError) => {
				if (currentError === GlobalError.MissingTaxInformation &&
					window.$cookies?.get(`cognito.missing-info-redirect.${org.value.Id}`) !== 'true' &&
					session.value?.enabledFeatures?.['RedirectToSettingsForMissingInformation']) {
					window.$cookies?.set(`cognito.missing-info-redirect.${org.value.Id}`, 'true', 0);
					router.push(error.value.link);
				}
			}, { immediate: true });

			const handleActionButton = async () => {
				actionButtonStatus.value = 'validating';
				error.value.action().then(() => {
					actionButtonStatus.value = 'success';
				}).catch(() => {
					actionButtonStatus.value = 'error';
				});
			};

			return {
				error,
				isOwner,
				handleActionButton,
				actionButtonStatus
			};
		}
	});
</script>
<style lang="scss">
	.global-errors {
		max-width: 100vw;
		border-top: 0;
		border-right: 0;
		border-left: 0;
		padding-right: 21px;

		:root:root & {
			margin-bottom: 0;
		}
	}
</style>